import React, {
    useEffect,
    useMemo,
} from "react";
import {Link, Redirect} from "react-router-dom";
import {withNamespaces} from "react-i18next";
import PageSeo from "../../seo/PageSeo";
import {
    useCurrentUserPlatformNewsReadState,
} from "../../../presentation/platform-news/use-current-user-platform-news-read-state";
import {
    defaultPlatformNewsPath,
} from "../../../content/platform-news";
import {platformNewsItems} from "./content/platform-news";
import "./style.less";

const publicSiteUrl = "https://svetofore.ru";

function PlatformNewsPage({t, match}) {
    const {
        userId,
        isRead,
        markAsRead,
    } = useCurrentUserPlatformNewsReadState();

    const activeNews = useMemo(
        () =>
            platformNewsItems.find(
                (newsItem) =>
                    newsItem.slug === match.params.newsSlug
            ) || null,
        [match.params.newsSlug]
    );

    useEffect(() => {
        if (userId && activeNews) {
            markAsRead(activeNews.id);
        }
    }, [
        activeNews,
        userId,
        markAsRead,
    ]);

    const canonicalUrl = activeNews
        ? `${publicSiteUrl}/news/${activeNews.slug}`
        : "";

    const imageUrl = activeNews
        ? `${publicSiteUrl}${activeNews.image}`
        : "";

    const structuredData = useMemo(() => {
        if (!activeNews) {
            return null;
        }

        return {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: activeNews.title,
            description: activeNews.seoDescription,
            image: [
                imageUrl,
                ...(activeNews.gallery || []).map(
                    (item) => `${publicSiteUrl}${item.src}`
                ),
            ],
            datePublished: activeNews.dateTime,
            dateModified: activeNews.modifiedTime,
            url: canonicalUrl,
            mainEntityOfPage: {
                "@type": "WebPage",
                "@id": canonicalUrl,
            },
            author: {
                "@type": "Organization",
                name: activeNews.author,
                url: publicSiteUrl,
            },
            publisher: {
                "@type": "Organization",
                name: "Светофор Логистика",
                url: publicSiteUrl,
            },
            articleSection: activeNews.category,
            inLanguage: "ru-RU",
            isAccessibleForFree: true,
        };
    }, [activeNews, canonicalUrl, imageUrl]);

    if (!activeNews) {
        return <Redirect to={defaultPlatformNewsPath}/>;
    }

    const ActiveArticle = activeNews.Article;

    return (
        <>
            <PageSeo
                title={activeNews.seoTitle}
                description={activeNews.seoDescription}
                imageUrl={imageUrl}
                imageAlt={activeNews.imageAlt}
                canonicalUrl={canonicalUrl}
                type="article"
                author={activeNews.author}
                publishedTime={activeNews.dateTime}
                modifiedTime={activeNews.modifiedTime}
                structuredData={structuredData}
            />

            <main className="platform-news-page">
                <section
                    className="platform-news-page__hero"
                    aria-hidden="true"
                >
                    <div className="container public-site-shell">
                        <div className="platform-news-page__hero-mark">
                            <span/>
                            <span/>
                        </div>
                    </div>
                </section>

                <div className="platform-news-page__workspace">
                    <div className="container public-site-shell">
                        <div className="platform-news-page__layout">
                            <aside className="platform-news-sidebar">
                                <div className="platform-news-sidebar__heading">
                                    <span className="platform-news-sidebar__heading-mark"/>

                                    <strong>
                                        {t(
                                            "main.newsPage.sidebarTitle"
                                        )}
                                    </strong>
                                </div>

                                <nav
                                    className="platform-news-sidebar__list"
                                    aria-label={t(
                                        "main.newsPage.navigationAriaLabel"
                                    )}
                                >
                                    {platformNewsItems.map(
                                        (newsItem) => {
                                            const isActive =
                                                newsItem.id ===
                                                activeNews.id;

                                            const newsIsRead = isRead(newsItem.id);

                                            return (
                                                <Link
                                                    className={`platform-news-sidebar__item${
                                                        isActive
                                                            ? " platform-news-sidebar__item--active"
                                                            : ""
                                                    }`}
                                                    to={`/news/${newsItem.slug}`}
                                                    aria-current={
                                                        isActive
                                                            ? "page"
                                                            : undefined
                                                    }
                                                    aria-controls="platform-news-reader"
                                                    key={newsItem.id}
                                                >
                                                    <div className="platform-news-sidebar__item-meta">
                                                        <time dateTime={newsItem.dateTime}>
                                                            {newsItem.date}
                                                        </time>

                                                        <span className="platform-news-sidebar__content-type">
                                                            {newsItem.contentType}
                                                        </span>

                                                        {userId && (
                                                            <span
                                                                className={`platform-news-sidebar__read-status platform-news-sidebar__read-status--${
                                                                    newsIsRead
                                                                        ? "read"
                                                                        : "unread"
                                                                }`}
                                                                role="img"
                                                                aria-label={t(
                                                                    newsIsRead
                                                                        ? "main.newsPage.read"
                                                                        : "main.newsPage.unread"
                                                                )}
                                                                title={t(
                                                                    newsIsRead
                                                                        ? "main.newsPage.read"
                                                                        : "main.newsPage.unread"
                                                                )}
                                                            />
                                                        )}
                                                    </div>

                                                    <strong>
                                                        {newsItem.sidebarTitle}
                                                    </strong>
                                                </Link>
                                            );
                                        }
                                    )}
                                </nav>
                            </aside>

                            <section
                                className="platform-news-reader"
                                id="platform-news-reader"
                            >
                                <ActiveArticle
                                    newsItem={activeNews}
                                />
                            </section>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}

export default withNamespaces("translation")(
    PlatformNewsPage
);
